import { lazy, Suspense, useEffect, useState } from 'react';
import { NavLink, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth.js';
import { useCollection } from './hooks/useCollection.js';
import { Login } from './pages/Login.jsx';
import { LiveFeed } from './pages/LiveFeed.jsx';

// Lazy-loaded routes — keep LiveFeed (default landing) eager.
const Campaigns      = lazy(() => import('./pages/Campaigns.jsx').then(m => ({ default: m.Campaigns })));
const CampaignDetail = lazy(() => import('./pages/CampaignDetail.jsx').then(m => ({ default: m.CampaignDetail })));
const Contacts       = lazy(() => import('./pages/Contacts.jsx').then(m => ({ default: m.Contacts })));
const ContactDetail  = lazy(() => import('./pages/ContactDetail.jsx').then(m => ({ default: m.ContactDetail })));
// [contact-hygiene] dedup tooling
const ContactDedup   = lazy(() => import('./pages/ContactDedup.jsx').then(m => ({ default: m.ContactDedup })));
const Emails         = lazy(() => import('./pages/Emails.jsx').then(m => ({ default: m.Emails })));
const EmailDetail    = lazy(() => import('./pages/EmailDetail.jsx').then(m => ({ default: m.EmailDetail })));
const LinkBuilder    = lazy(() => import('./pages/LinkBuilder.jsx').then(m => ({ default: m.LinkBuilder })));
const PDFBuilder     = lazy(() => import('./pages/PDFBuilder.jsx').then(m => ({ default: m.PDFBuilder })));
const Views          = lazy(() => import('./pages/Views.jsx').then(m => ({ default: m.Views })));
const Svgs           = lazy(() => import('./pages/Svgs.jsx').then(m => ({ default: m.Svgs })));
const Analytics      = lazy(() => import('./pages/Analytics.jsx').then(m => ({ default: m.Analytics })));
const Reputation     = lazy(() => import('./pages/Reputation.jsx').then(m => ({ default: m.Reputation })));
const AbTests        = lazy(() => import('./pages/AbTests.jsx').then(m => ({ default: m.AbTests })));
const Sequences      = lazy(() => import('./pages/Sequences.jsx').then(m => ({ default: m.Sequences })));
const Settings       = lazy(() => import('./pages/Settings.jsx').then(m => ({ default: m.Settings })));
const Warmup         = lazy(() => import('./pages/Warmup.jsx').then(m => ({ default: m.Warmup })));
const Replies        = lazy(() => import('./pages/Replies.jsx').then(m => ({ default: m.Replies })));
const Dispatch       = lazy(() => import('./pages/Dispatch.jsx').then(m => ({ default: m.Dispatch })));
const Templates      = lazy(() => import('./pages/Templates.jsx').then(m => ({ default: m.Templates })));
const LinkedInInbox  = lazy(() => import('./pages/LinkedInInbox.jsx').then(m => ({ default: m.LinkedInInbox })));
// [NEW FEATURE 1, 3, 4, 9] new dashboard routes.
const Suppressions   = lazy(() => import('./pages/Suppressions.jsx').then(m => ({ default: m.Suppressions })));
const Signatures     = lazy(() => import('./pages/Signatures.jsx').then(m => ({ default: m.Signatures })));
const Attachments    = lazy(() => import('./pages/Attachments.jsx').then(m => ({ default: m.Attachments })));
const Scheduler      = lazy(() => import('./pages/Scheduler.jsx').then(m => ({ default: m.Scheduler })));
// [LinkedIn research suite] + [Industry targets catalogue]
const LinkedInResearch = lazy(() => import('./pages/LinkedInResearch.jsx').then(m => ({ default: m.LinkedInResearch })));
const IndustryTargets  = lazy(() => import('./pages/IndustryTargets.jsx').then(m => ({ default: m.IndustryTargets })));
// [Quick trackers] paste N names → get N pixels back instantly
const QuickTrackers    = lazy(() => import('./pages/QuickTrackers.jsx').then(m => ({ default: m.QuickTrackers })));
// [Tracker test] diagnostic page — fire pixels, watch live events, probe URLs
const TrackerTest      = lazy(() => import('./pages/TrackerTest.jsx').then(m => ({ default: m.TrackerTest })));
// [Data inventory] at-a-glance: which collections have data, which are empty
const DataInventory    = lazy(() => import('./pages/DataInventory.jsx').then(m => ({ default: m.DataInventory })));
// [AI tools] NVIDIA Llama subject-line tester + lead-intel brief generator.
const AiTools          = lazy(() => import('./pages/AiTools.jsx').then(m => ({ default: m.AiTools })));
// [compliance] Audit log — append-only system + manual compliance events.
const AuditLog         = lazy(() => import('./pages/AuditLog.jsx').then(m => ({ default: m.AuditLog })));
// [NEW FEATURE 6] Command palette.
import { CommandPalette } from './components/CommandPalette.jsx';
// [AI chat] Floating context-aware assistant (Cmd+/ or Ctrl+/).
import { AiChatWidget } from './components/AiChatWidget.jsx';

const THEMES = [
  { key: 'mckinsey', label: 'Advisory',  hint: 'McKinsey navy + accent blue' },
  { key: 'luxury',   label: 'Maison',    hint: 'Burgundy + rose-gold on champagne' },
  { key: 'gunmetal', label: 'Gunmetal',  hint: 'Dark slate + amber' },
];

// Sectioned navigation — each item maps 1:1 to an existing route.
const NAV_SECTIONS = [
  {
    label: 'Send',
    items: [
      { to: '/',           end: true, label: 'Tape' },
      { to: '/quick-trackers',        label: '⚡ Quick trackers' },
      { to: '/tracker-test',          label: '🧪 Tracker test' },
      { to: '/dispatch',              label: 'Dispatch' },
      { to: '/templates',             label: 'Templates' },
      { to: '/emails',                label: 'Emails' },
      { to: '/sequences',             label: 'Sequences' },
      { to: '/warmup',                label: 'Warmup' },
    ],
  },
  {
    label: 'Audience',
    items: [
      { to: '/contacts',              label: 'Contacts' },
      { to: '/contacts-dedup',        label: 'Find duplicates' },
      { to: '/replies',               label: 'Replies' },
      { to: '/linkedin',              label: 'LinkedIn' },
      { to: '/linkedin-research',     label: 'LinkedIn research' },
      { to: '/industry-targets',      label: 'Industry targets' },
    ],
  },
  {
    label: 'Tracking',
    items: [
      { to: '/views',                 label: 'Views' },
      { to: '/svgs',                  label: 'Beacons' },
      { to: '/links',                 label: 'Links' },
      { to: '/pdfs',                  label: 'Documents' },
      { to: '/ab-tests',              label: 'A/B Tests' },
    ],
  },
  {
    label: 'Insights',
    items: [
      { to: '/analytics',             label: 'Analytics' },
      { to: '/reputation',            label: 'Reputation' },
      { to: '/campaigns',             label: 'Campaigns' },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/data-inventory',        label: '📊 Data inventory' },
      { to: '/ai-tools',              label: 'AI Tools' },
      { to: '/audit-log',             label: 'Audit log' },
      { to: '/settings',              label: 'Settings' },
    ],
  },
];

// Default theme — Gunmetal is the house style. Switcher still allows override
// and the user's selection is persisted in localStorage across reloads.
const DEFAULT_THEME = 'gunmetal';

function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem('pt_theme') || DEFAULT_THEME);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('pt_theme', theme);
  }, [theme]);
  return [theme, setTheme];
}

export default function App() {
  const { user, loading, signOut } = useAuth();
  const [theme, setTheme] = useTheme();
  if (loading) return <Splash />;
  if (!user) return <Login />;
  return <Shell user={user} signOut={signOut} theme={theme} setTheme={setTheme} />;
}

function Shell({ user, signOut, theme, setTheme }) {
  const { docs: events } = useCollection('events', { max: 50 });
  const [navOpen, setNavOpen] = useState(false);
  // [NEW FEATURE 6] Command-palette open state.
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const now = new Date();
  const last = events[0]?.occurred_at?.toDate?.() || null;
  const ago = last ? Math.max(0, Math.floor((now - last) / 1000)) : null;

  // Auto-close mobile drawer on route change.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  // [NEW FEATURE 6] Global ⌘K / Ctrl-K listener.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="min-h-screen text-[12px]">
      {/* Sidebar */}
      <Sidebar
        theme={theme}
        user={user}
        signOut={signOut}
        open={navOpen}
        onClose={() => setNavOpen(false)}
      />

      {/* Mobile backdrop */}
      {navOpen && (
        <div
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 sidebar-backdrop"
          style={{ background: 'rgba(0,0,0,0.35)' }}
          aria-hidden="true"
        />
      )}

      {/* Main column — pushed right by sidebar on desktop */}
      <div className="flex flex-col min-h-screen sidebar-main">
        {/* Ticker */}
        <div
          className="px-4 py-1 flex items-center gap-4 text-[10px] tracking-[0.14em] uppercase overflow-x-auto whitespace-nowrap"
          style={{ background: 'var(--ticker-bg)', color: 'var(--ticker-fg)' }}
        >
          <button
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            className="sidebar-burger inline-flex items-center justify-center"
            style={{
              border: '1px solid currentColor',
              color: 'var(--ticker-fg)',
              background: 'transparent',
              width: 22,
              height: 18,
              padding: 0,
            }}
          >
            <span style={{ display: 'inline-block', width: 10, height: 1, background: 'currentColor', boxShadow: '0 -3px 0 currentColor, 0 3px 0 currentColor' }} />
          </button>
          <Tick k="Sys" v="Nominal" positive />
          <Tick k="Evt/50" v={String(events.length).padStart(3, '0')} />
          <Tick k="Last" v={ago == null ? '—' : ago < 60 ? `${ago}s` : `${Math.floor(ago/60)}m`} />
          <Tick k="UTC" v={now.toISOString().slice(11, 19)} mono />
          <Tick k="Date" v={now.toISOString().slice(0, 10)} mono />
          <Tick k="Rel" v="v3.0" mono />
          <Tick k="User" v={(user.email || user.uid).split('@')[0]} />
          <span className="flex-1" />
          <span style={{ opacity: 0.7 }}>Private · Confidential · Do not distribute</span>
        </div>

        {/* Masthead — slimmer now that nav lives in the sidebar */}
        <header style={{ background: 'var(--card)', borderBottom: '1px solid var(--rule)' }}>
          <div className="px-4 py-2.5 flex items-center justify-between gap-4">
            <div className="leading-tight">
              <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: 'var(--muted)' }}>
                {themeTagline(theme)}
              </div>
              <div className="display text-[14px] font-semibold tracking-tight" style={{ color: 'var(--ink)' }}>
                {currentSectionLabel(location.pathname)}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <ThemeSwitcher theme={theme} setTheme={setTheme} />
            </div>
          </div>
          <div className="theme-brand-bar" />
        </header>

        <main className="flex-1 px-4 py-4 w-full max-w-[1600px] mx-auto">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/"              element={<LiveFeed />} />
              <Route path="/campaigns"     element={<Campaigns />} />
              <Route path="/campaigns/:id" element={<CampaignDetail />} />
              <Route path="/contacts"      element={<Contacts />} />
              <Route path="/contacts-dedup" element={<ContactDedup />} />
              <Route path="/contacts/:id"  element={<ContactDetail />} />
              <Route path="/emails"        element={<Emails />} />
              <Route path="/emails/:id"    element={<EmailDetail />} />
              <Route path="/links"         element={<LinkBuilder />} />
              <Route path="/pdfs"          element={<PDFBuilder />} />
              <Route path="/views"         element={<Views />} />
              <Route path="/dispatch"      element={<Dispatch />} />
              <Route path="/templates"     element={<Templates />} />
              <Route path="/svgs"          element={<Svgs />} />
              <Route path="/analytics"     element={<Analytics />} />
              <Route path="/reputation"    element={<Reputation />} />
              <Route path="/ab-tests"      element={<AbTests />} />
              <Route path="/ab-tests/:id"  element={<AbTests />} />
              <Route path="/sequences"     element={<Sequences />} />
              <Route path="/replies"       element={<Replies />} />
              <Route path="/linkedin"      element={<LinkedInInbox />} />
              <Route path="/warmup"        element={<Warmup />} />
              <Route path="/settings"      element={<Settings />} />
              {/* [NEW FEATURE 1] */}
              <Route path="/suppressions"  element={<Suppressions />} />
              {/* [NEW FEATURE 3] */}
              <Route path="/signatures"    element={<Signatures />} />
              {/* [NEW FEATURE 4] */}
              <Route path="/attachments"   element={<Attachments />} />
              {/* [NEW FEATURE 9] */}
              <Route path="/scheduler"     element={<Scheduler />} />
              {/* [LinkedIn research suite] */}
              <Route path="/linkedin-research" element={<LinkedInResearch />} />
              {/* [Industry targets catalogue] */}
              <Route path="/industry-targets"  element={<IndustryTargets />} />
              {/* [AI tools] NVIDIA Llama subject tester + lead intel brief. */}
              <Route path="/ai-tools"          element={<AiTools />} />
              {/* [compliance] System + manual audit-log browser. */}
              <Route path="/audit-log"         element={<AuditLog />} />
              {/* [Quick trackers] paste N names → get N pixels back instantly */}
              <Route path="/quick-trackers"    element={<QuickTrackers />} />
              {/* [Tracker test] diagnostic — fire pixels, watch events live */}
              <Route path="/tracker-test"      element={<TrackerTest />} />
              {/* [Data inventory] at-a-glance collection counts */}
              <Route path="/data-inventory"    element={<DataInventory />} />
              <Route path="*"              element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>

        <footer
          className="text-[9px] uppercase tracking-[0.16em] px-4 py-1.5 flex items-center justify-between"
          style={{ borderTop: '1px solid var(--rule)', color: 'var(--muted)' }}
        >
          <span>© MMXXVI Phantom · All times UTC</span>
          <span className="font-mono normal-case tracking-normal">{new Date().toISOString()}</span>
        </footer>
      </div>

      {/* [NEW FEATURE 6] Global command palette. */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} uid={user?.uid} />

      {/* [AI chat] Floating assistant — Cmd+/ or Ctrl+/ to summon. */}
      <AiChatWidget />

      {/* Sidebar layout rules — kept inline so we don't touch index.css. */}
      <style>{sidebarStyles}</style>
    </div>
  );
}

function Sidebar({ theme, user, signOut, open, onClose }) {
  return (
    <aside
      className={`sidebar ${open ? 'sidebar--open' : ''}`}
      style={{
        background: 'var(--card)',
        borderRight: '1px solid var(--rule)',
        color: 'var(--body)',
      }}
      aria-label="Primary"
    >
      {/* Brand */}
      <div
        className="px-4 py-3 flex items-center gap-3"
        style={{ borderBottom: '1px solid var(--rule)' }}
      >
        <Emblem />
        <div className="leading-tight flex-1 min-w-0">
          <div className="display text-[15px] font-semibold tracking-tight truncate" style={{ color: 'var(--ink)' }}>
            Phantom Track
          </div>
          <div className="text-[9px] uppercase tracking-[0.22em] truncate" style={{ color: 'var(--muted)' }}>
            {themeTagline(theme)}
          </div>
        </div>
        <button
          onClick={onClose}
          className="sidebar-close text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5"
          style={{
            border: '1px solid var(--rule)',
            color: 'var(--muted)',
            background: 'transparent',
          }}
          aria-label="Close navigation"
        >
          ×
        </button>
      </div>

      {/* Sections */}
      <nav className="flex-1 overflow-y-auto py-2">
        {NAV_SECTIONS.map((section) => (
          <div key={section.label} className="px-2 pt-3 pb-1">
            <div
              className="px-2 pb-1.5 text-[9px] uppercase tracking-[0.22em]"
              style={{ color: 'var(--dim)' }}
            >
              {section.label}
            </div>
            <ul className="flex flex-col">
              {section.items.map((n) => (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    end={n.end}
                    className={({ isActive }) =>
                      `sidebar-link block px-2.5 py-1.5 text-[11px] tracking-[0.06em] ${
                        isActive ? 'sidebar-link--active' : ''
                      }`
                    }
                    style={({ isActive }) => ({
                      color: isActive ? 'var(--btn-fg)' : 'var(--body)',
                      background: isActive ? 'var(--btn-bg)' : 'transparent',
                      fontWeight: isActive ? 600 : 400,
                      borderLeft: `2px solid ${isActive ? 'var(--brand-bar)' : 'transparent'}`,
                    })}
                  >
                    {n.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer — user pill + exit */}
      <div
        className="px-3 py-3 flex items-center gap-2"
        style={{ borderTop: '1px solid var(--rule)' }}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-[0.18em]" style={{ color: 'var(--muted)' }}>
            Signed in
          </div>
          <div
            className="text-[11px] truncate font-mono"
            style={{ color: 'var(--ink)' }}
            title={user.email || user.uid}
          >
            {user.email || user.uid}
          </div>
        </div>
        <button
          onClick={signOut}
          className="text-[10px] uppercase tracking-[0.14em] px-2 py-1"
          style={{
            border: '1px solid var(--rule)',
            color: 'var(--muted)',
            background: 'transparent',
          }}
        >
          Exit
        </button>
      </div>
    </aside>
  );
}

function ThemeSwitcher({ theme, setTheme }) {
  return (
    <div className="flex" style={{ border: '1px solid var(--rule)' }}>
      {THEMES.map((t, i) => (
        <button
          key={t.key}
          title={t.hint}
          onClick={() => setTheme(t.key)}
          className="px-2 py-0.5 text-[10px] uppercase tracking-[0.14em]"
          style={{
            borderLeft: i ? '1px solid var(--rule)' : 'none',
            background: theme === t.key ? 'var(--btn-bg)' : 'transparent',
            color:      theme === t.key ? 'var(--btn-fg)' : 'var(--muted)',
            fontWeight: theme === t.key ? 600 : 400,
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Tick({ k, v, positive, mono }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span style={{ opacity: 0.6 }}>{k}</span>
      <span
        className={`${mono ? 'font-mono' : ''} font-semibold`}
        style={positive ? { color: 'var(--positive)' } : undefined}
      >{v}</span>
    </span>
  );
}

function themeTagline(t) {
  if (t === 'luxury')   return 'La Maison · Correspondence Intelligence';
  if (t === 'gunmetal') return 'SIGINT · Outbound Tracking Suite';
  return 'Advisory · Outbound Engagement Research';
}

// Reverse lookup — masthead title for the active route.
function currentSectionLabel(pathname) {
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.end ? pathname === item.to : pathname.startsWith(item.to)) {
        return item.label;
      }
    }
  }
  return 'Phantom Track';
}

function Emblem() {
  return (
    <svg width="28" height="28" viewBox="0 0 34 34" className="shrink-0">
      <rect x="0.5" y="0.5" width="33" height="33" fill="none" stroke="currentColor" strokeWidth="1"
            style={{ color: 'var(--ink)' }} />
      <text x="17" y="23" textAnchor="middle"
            fontFamily="var(--display-font)"
            fontSize="16" fontWeight="700" fill="currentColor"
            style={{ color: 'var(--ink)' }}>P</text>
    </svg>
  );
}

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center text-[10px] uppercase tracking-[0.22em]"
         style={{ color: 'var(--muted)' }}>
      Initialising Terminal…
    </div>
  );
}

function RouteFallback() {
  return (
    <div className="px-2 py-6 text-[10px] uppercase tracking-[0.22em]"
         style={{ color: 'var(--muted)' }}>
      Loading…
    </div>
  );
}

// Sidebar layout — desktop fixed rail, mobile slide-in drawer.
const sidebarStyles = `
.sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  width: 232px;
  display: flex;
  flex-direction: column;
  z-index: 40;
  transform: translateX(0);
  transition: transform 180ms ease;
}
.sidebar-main {
  margin-left: 232px;
}
.sidebar-burger { display: none; }
.sidebar-close  { display: none; }
.sidebar-link:hover:not(.sidebar-link--active) {
  background: var(--sunken);
  color: var(--ink) !important;
}
@media (max-width: 760px) {
  .sidebar {
    transform: translateX(-100%);
    box-shadow: 0 0 24px rgba(0, 0, 0, 0.18);
  }
  .sidebar--open { transform: translateX(0); }
  .sidebar-main { margin-left: 0; }
  .sidebar-burger { display: inline-flex; }
  .sidebar-close  { display: inline-flex; }
}
`;
